import { HttpClient } from '@adapters/HttpClient';
import {
  PreInvoice,
  PreInvoiceShipment,
  PreInvoiceShipmentsPage,
  PreInvoiceStorage,
  PreInvoiceStoragesPage,
} from '../models/ViewPreInvoices';

export interface ViewPreInvoicesFilters {
  searchFilter?: { filters: any[]; logic: string };
  search?: string;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

export interface PreInvoiceShipmentsFilters {
  preInvoiceId: number;
  searchFilter?: { filters: any[]; logic: string };
  search?: string;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

export interface PreInvoiceStoragesFilters {
  preInvoiceId: number;
  searchFilter?: { filters: any[]; logic: string };
  search?: string;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

export interface ExportViewPreInvoicesPayload {
  advanced_filters?: {
    filters: Array<{
      column: string;
      operator: string;
      value?: any;
    }>;
    logic: string;
  };
  sort_by?: string;
  sort_direction?: 'asc' | 'desc';
}

export interface ExportViewPreInvoicesResponse {
  url_file: string;
}

export interface ViewPreInvoicesPage {
  viewPreInvoices: PreInvoice[];
  nextPage?: number;
  hasMore: boolean;
  total: number;
}

function buildAdvancedFiltersParams(searchFilter: {
  filters: any[];
  logic: string;
}) {
  const params: Record<string, any> = {};
  if (searchFilter.filters && Array.isArray(searchFilter.filters)) {
    searchFilter.filters.forEach((f, i) => {
      params[`advanced_filters[filters][${i}][column]`] = f.column;
      params[`advanced_filters[filters][${i}][operator]`] = f.operator;
      // Solo enviar value si existe (para operadores is_null / is_not_null no debe enviarse)
      if (
        f.value !== undefined &&
        f.value !== null &&
        !(Array.isArray(f.value) && f.value.length === 0)
      ) {
        params[`advanced_filters[filters][${i}][value]`] = f.value;
      }
    });
  }
  if (searchFilter.logic) {
    params['advanced_filters[logic]'] = searchFilter.logic;
  }
  return params;
}

// ?? Construye los par�metros para exportaci�n exactamente como el GET de ViewPreInvoices (lista)
function buildExportParamsFromPayload(payload: ExportViewPreInvoicesPayload) {
  const params: Record<string, any> = {};

  if (payload.advanced_filters) {
    const af = buildAdvancedFiltersParams(payload.advanced_filters as any);
    Object.assign(params, af);
  }

  const sortBy = payload.sort_by || 'id';
  const sortDirection = payload.sort_direction || 'desc';
  params['sorting[column]'] = sortBy;
  params['sorting[direction]'] = sortDirection;

  if (payload.status_ids !== undefined && payload.status_ids !== null) {
    params.status_ids = payload.status_ids;
  }

  return params;
}

export class ViewPreInvoicesApiAdapter {
  constructor(private httpClient: HttpClient) {}

  async getViewPreInvoicesPaginated(filters: ViewPreInvoicesFilters = {}): Promise<ViewPreInvoicesPage> {
    const {
      searchFilter,
      search,
      sortBy = 'id',
      sortDirection = 'desc',
      page = 1,
      limit = 15,
    } = filters;

    const params: any = {
      ...(sortBy && { 'sorting[column]': sortBy }),
      ...(sortDirection && { 'sorting[direction]': sortDirection }),
      page,
      per_page: limit,
      ...(search ? { search } : {}),
      ...(searchFilter ? buildAdvancedFiltersParams(searchFilter) : {}),
    };

    // Solo mostrar loading global para la primera página
    const showGlobalLoading = page === 1;
    const response = await this.httpClient.get<any>(
      '/invoices/pre-invoices',
      params,
      {},
      showGlobalLoading
    );

    return {
      viewPreInvoices: response.data,
      nextPage:
        response.meta.currentPage < response.meta.lastPage
          ? response.meta.currentPage + 1
          : undefined,
      hasMore: response.meta.currentPage < response.meta.lastPage,
      total: response.meta.total,
    };
  }

  async getAll(): Promise<PreInvoice[]> {
    const response = await this.getViewPreInvoicesPaginated();
    return response.viewPreInvoices;
  }

  async getPreInvoiceById(id: number): Promise<PreInvoice> {
    const response = await this.httpClient.get<PreInvoice>(`/invoices/pre-invoices/${id}`);
    return response;
  }

  async getPreInvoiceShipmentsPaginated(
    filters: PreInvoiceShipmentsFilters
  ): Promise<PreInvoiceShipmentsPage> {
    const {
      preInvoiceId,
      searchFilter,
      search,
      sortBy = 'id',
      sortDirection = 'desc',
      page = 1,
      limit = 25,
    } = filters;

    const params: Record<string, any> = {
      ...(sortBy && { 'sorting[column]': sortBy }),
      ...(sortDirection && { 'sorting[direction]': sortDirection }),
      page,
      per_page: limit,
      ...(search ? { search } : {}),
      ...(searchFilter ? buildAdvancedFiltersParams(searchFilter) : {}),
    };

    const showGlobalLoading = page === 1;
    const response = await this.httpClient.get<{
      data: PreInvoiceShipment[];
      meta: {
        total?: number;
        currentPage?: number;
        current_page?: number;
        lastPage?: number;
        last_page?: number;
      };
    }>(
      `/invoices/pre-invoices/${preInvoiceId}/shipments`,
      params,
      {},
      showGlobalLoading
    );

    const currentPage = response.meta?.currentPage ?? response.meta?.current_page ?? 1;
    const lastPage = response.meta?.lastPage ?? response.meta?.last_page ?? currentPage;
    const total = response.meta?.total ?? 0;

    return {
      shipments: response.data ?? [],
      nextPage: currentPage < lastPage ? currentPage + 1 : undefined,
      hasMore: currentPage < lastPage,
      total,
    };
  }

  async getPreInvoiceStoragesPaginated(
    filters: PreInvoiceStoragesFilters
  ): Promise<PreInvoiceStoragesPage> {
    const {
      preInvoiceId,
      searchFilter,
      search,
      sortBy = 'product_id',
      sortDirection = 'asc',
      page = 1,
      limit = 25,
    } = filters;

    const params: Record<string, any> = {
      ...(sortBy && { 'sorting[column]': sortBy }),
      ...(sortDirection && { 'sorting[direction]': sortDirection }),
      page,
      per_page: limit,
      ...(search ? { search } : {}),
      ...(searchFilter ? buildAdvancedFiltersParams(searchFilter) : {}),
    };

    const showGlobalLoading = page === 1;
    const response = await this.httpClient.get<{
      data: PreInvoiceStorage[];
      meta: {
        total?: number;
        currentPage?: number;
        current_page?: number;
        lastPage?: number;
        last_page?: number;
      };
    }>(
      `/invoices/pre-invoices/${preInvoiceId}/storages`,
      params,
      {},
      showGlobalLoading
    );

    const currentPage = response.meta?.currentPage ?? response.meta?.current_page ?? 1;
    const lastPage = response.meta?.lastPage ?? response.meta?.last_page ?? currentPage;
    const total = response.meta?.total ?? 0;

    return {
      storages: response.data ?? [],
      nextPage: currentPage < lastPage ? currentPage + 1 : undefined,
      hasMore: currentPage < lastPage,
      total,
    };
  }

  async exportViewPreInvoices(
    payload: ExportViewPreInvoicesPayload
  ): Promise<ExportViewPreInvoicesResponse> {
    const params = buildExportParamsFromPayload(payload);
    return await this.httpClient.post('/invoices/pre-invoices/export', params);
  }

  /**
   * Facturar una prefactura (marcar como CLOSED)
   * @param id - ID de la prefactura
   * @returns Promise con respuesta de la API
   */
  async invoicePreInvoice(id: number): Promise<{ success: boolean; message: string }> {
    try {
      const response = await this.httpClient.post<any>(`/invoices/pre-invoices/${id}/invoice`, {});
      
      // 🎯 Si la API devuelve 200 OK sin la propiedad 'success', considerarlo éxito
      if (response && typeof response.success === 'undefined') {
        return { 
          success: true, 
          message: response.message || 'Prefactura facturada exitosamente'
        };
      }
      
      return response;
    } catch (error: any) {
      // 🎯 Manejar errores de la API
      if (error.response?.data) {
        const errorData = error.response.data;
        
        // La API devuelve el error en errorData.error.message
        if (errorData.error && errorData.error.message) {
          return {
            success: false,
            message: errorData.error.message,
          };
        }
        
        // Si hay un mensaje de error general
        if (errorData.message) {
          return {
            success: false,
            message: errorData.message,
          };
        }
      }
      
      // Error genérico
      return {
        success: false,
        message: error.message || 'Error al facturar la prefactura',
      };
    }
  }

  /**
   * Remover un storage de una prefactura
   * POST /api/v1/accounting/storages/{id}/remove-from-preinvoice
   * @param storageId - ID del storage
   * @param reason - Razón opcional para la eliminación (recomendado para auditoría)
   * @returns Promise con respuesta de la API
   */
  async removeStorageFromPreInvoice(
    storageId: number,
    reason?: string
  ): Promise<{ 
    status: string;
    id: number;
    preinvoice_id: number | null;
  }> {
    try {
      const body = reason ? { reason } : {};
      const response = await this.httpClient.post<any>(
        `/accounting/storages/${storageId}/remove-from-preinvoice`,
        body
      );
      
      return {
        status: response.status || 'ok',
        id: response.id || storageId,
        preinvoice_id: response.preinvoice_id ?? null,
      };
    } catch (error: any) {
      // 🎯 Manejar errores de la API
      if (error.response?.data) {
        const errorData = error.response.data;
        
        // La API devuelve el error en errorData.error.message
        if (errorData.error && errorData.error.message) {
          throw new Error(errorData.error.message);
        }
        
        // Si hay un mensaje de error general
        if (errorData.message) {
          throw new Error(errorData.message);
        }
      }
      
      // Error genérico
      throw new Error(error.message || 'Error al remover el storage de la prefactura');
    }
  }
}




